/**
 * Partial Repayment Calculator Form Component
 * 部分還款計算器表單組件
 */
import { useState } from 'react';
import { useCustomTranslation } from '../../../hooks';
import { trackPartialRepaymentCalculation } from '../../../utils/analytics';

interface PartialRepaymentFormProps {
  onCalculate: (data: PartialRepaymentData) => void;
  isLoading: boolean;
}

export interface PartialRepaymentData {
  outstanding_balance: number;
  annual_interest_rate: number;
  remaining_instalments: number;
  current_instalment_amount: number;
  frequency: 'monthly' | 'fortnightly';
  partial_repayment_amount: number;
  keep_instalment_amount: boolean;
}

export default function PartialRepaymentForm({
  onCalculate,
  isLoading,
}: PartialRepaymentFormProps) {
  const { t } = useCustomTranslation();
  
  const [formData, setFormData] = useState<PartialRepaymentData>({
    outstanding_balance: 1000000,
    annual_interest_rate: 3.5,
    remaining_instalments: 240,
    current_instalment_amount: 6000,
    frequency: 'monthly',
    partial_repayment_amount: 100000,
    keep_instalment_amount: false, // Default: reduce instalment amount
  });

  // String representations for input fields (allows free typing)
  const [inputValues, setInputValues] = useState({
    outstanding_balance: '1000000',
    annual_interest_rate: '3.5',
    remaining_instalments: '240',
    current_instalment_amount: '6000',
    partial_repayment_amount: '100000',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track the calculation event
    trackPartialRepaymentCalculation({
      outstanding_balance: formData.outstanding_balance,
      partial_repayment_amount: formData.partial_repayment_amount,
      frequency: formData.frequency,
      option_type: formData.keep_instalment_amount ? 'reduce_period' : 'reduce_payment',
    });
    
    onCalculate(formData);
  };

  const handleNumberInput = (field: keyof typeof inputValues, value: string) => {
    // Only allow numbers and decimal point
    const sanitized = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const finalValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    
    // Update the string value
    setInputValues((prev) => ({
      ...prev,
      [field]: finalValue,
    }));
    
    // Update the actual form data with parsed number
    const numValue = finalValue === '' || finalValue === '.' ? 0 : parseFloat(finalValue);
    if (!isNaN(numValue)) {
      setFormData((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    }
  };

  const handleInputChange = (field: keyof PartialRepaymentData, value: string | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="loan-calculator-form">
      {/* Current Repayment Details Section */}
      <div className="form-section">
        <h3>{t('partialRepayment.currentDetails')}</h3>

        <div className="form-group">
          <label htmlFor="frequency">{t('partialRepayment.repaymentPlan')}</label>
          <div className="radio-group-horizontal">
            <label>
              <input
                type="radio"
                name="frequency"
                value="monthly"
                checked={formData.frequency === 'monthly'}
                onChange={(e) => handleInputChange('frequency', e.target.value as 'monthly' | 'fortnightly')}
              />
              {t('frequency.monthly')}
            </label>
            <label>
              <input
                type="radio"
                name="frequency"
                value="fortnightly"
                checked={formData.frequency === 'fortnightly'}
                onChange={(e) => handleInputChange('frequency', e.target.value as 'monthly' | 'fortnightly')}
              />
              {t('frequency.fortnightly')}
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="outstanding_balance">
            {t('partialRepayment.outstandingBalance')} (HKD)
          </label>
          <input
            id="outstanding_balance"
            type="text"
            inputMode="decimal"
            value={inputValues.outstanding_balance}
            onChange={(e) => handleNumberInput('outstanding_balance', e.target.value)}
            placeholder={t('partialRepayment.outstandingBalancePlaceholder')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="annual_interest_rate">
            {t('partialRepayment.interestRate')} (%)
          </label>
          <input
            id="annual_interest_rate"
            type="text"
            inputMode="decimal"
            value={inputValues.annual_interest_rate}
            onChange={(e) => handleNumberInput('annual_interest_rate', e.target.value)}
            placeholder={t('partialRepayment.interestRatePlaceholder')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="remaining_instalments">
            {t('partialRepayment.remainingInstalments')}
          </label>
          <input
            id="remaining_instalments"
            type="text"
            inputMode="numeric"
            value={inputValues.remaining_instalments}
            onChange={(e) => handleNumberInput('remaining_instalments', e.target.value)}
            placeholder={t('partialRepayment.remainingInstalmentsPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="current_instalment_amount">
            {t('partialRepayment.instalmentAmount')} (HKD)
          </label>
          <input
            id="current_instalment_amount"
            type="text"
            inputMode="decimal"
            value={inputValues.current_instalment_amount}
            onChange={(e) => handleNumberInput('current_instalment_amount', e.target.value)}
            placeholder={t('partialRepayment.instalmentAmountPlaceholder')}
          />
        </div>
      </div>

      {/* Partial Repayment Section */}
      <div className="form-section">
        <h3>{t('partialRepayment.partialRepaymentTitle')}</h3>

        <div className="form-group">
          <label htmlFor="partial_repayment_amount">
            {t('partialRepayment.partialAmount')} (HKD)
          </label>
          <input
            id="partial_repayment_amount"
            type="text"
            inputMode="decimal"
            value={inputValues.partial_repayment_amount}
            onChange={(e) => handleNumberInput('partial_repayment_amount', e.target.value)}
            placeholder={t('partialRepayment.partialAmountPlaceholder')}
          />
        </div>

        <div className="form-group">
          <label>{t('partialRepayment.afterRepayment')}</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="repayment_option"
                checked={!formData.keep_instalment_amount}
                onChange={() => handleInputChange('keep_instalment_amount', false)}
              />
              {t('partialRepayment.reduceInstalment')}
            </label>
            <label>
              <input
                type="radio"
                name="repayment_option"
                checked={formData.keep_instalment_amount}
                onChange={() => handleInputChange('keep_instalment_amount', true)}
              />
              {t('partialRepayment.reducePeriod')}
            </label>
          </div>
        </div>
      </div>

      <button type="submit" disabled={isLoading}>
        {isLoading ? t('form.calculating') : t('form.calculate')}
      </button>
    </form>
  );
}
